import { useEffect, useRef, useState, type FormEvent } from "react";
import type { CoachThread } from "../schema";

interface Props {
  threads: CoachThread[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onRename: (id: string, title: string) => void;
  onDelete: (id: string) => void;
}

function formatDate(ms: number): string {
  if (!ms) return "";
  const d = new Date(ms);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function ThreadSwitcher({
  threads,
  activeId,
  onSelect,
  onNew,
  onRename,
  onDelete,
}: Props) {
  const [open, setOpen] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renamingValue, setRenamingValue] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  const active = threads.find((t) => t.id === activeId) ?? null;

  // Click-outside / Escape close.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      // Only close the dropdown on Escape when nothing is being renamed —
      // the rename input handles its own Escape to cancel-without-saving.
      if (e.key === "Escape" && !renamingId) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, renamingId]);

  // Focus + select the rename input when entering rename mode.
  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  const startRename = (t: CoachThread) => {
    setRenamingId(t.id);
    setRenamingValue(t.title);
  };

  const commitRename = (t: CoachThread) => {
    const trimmed = renamingValue.trim();
    if (trimmed && trimmed !== t.title) {
      onRename(t.id, trimmed);
    }
    setRenamingId(null);
    setRenamingValue("");
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenamingValue("");
  };

  const onRenameSubmit = (e: FormEvent, t: CoachThread) => {
    e.preventDefault();
    commitRename(t);
  };

  return (
    <div className="coach__switcher" ref={rootRef}>
      <button
        type="button"
        className="coach__switcher-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="coach__switcher-label">Dossier</span>
        <span className="coach__switcher-title">
          {active ? active.title : "No thread yet"}
        </span>
        <span className="coach__switcher-chev" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div className="coach__switcher-panel" role="listbox">
          <button
            type="button"
            className="coach__switcher-new"
            onClick={() => {
              setOpen(false);
              onNew();
            }}
            disabled={renamingId !== null}
          >
            <span className="coach__switcher-new-plus">+</span>
            <span>New dossier</span>
          </button>
          {threads.length === 0 ? (
            <div className="coach__switcher-empty">
              No dossiers yet. Start one above.
            </div>
          ) : (
            <ul className="coach__switcher-list">
              {threads.map((t) => {
                const isActive = t.id === activeId;
                const isRenaming = renamingId === t.id;
                return (
                  <li
                    key={t.id}
                    className={`coach__switcher-item${
                      isActive ? " coach__switcher-item--active" : ""
                    }${isRenaming ? " coach__switcher-item--renaming" : ""}`}
                  >
                    {isRenaming ? (
                      <form
                        className="coach__switcher-item-rename"
                        onSubmit={(e) => onRenameSubmit(e, t)}
                      >
                        <input
                          ref={renameInputRef}
                          type="text"
                          className="coach__switcher-item-input"
                          value={renamingValue}
                          onChange={(e) => setRenamingValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") {
                              e.preventDefault();
                              e.stopPropagation();
                              cancelRename();
                            }
                          }}
                          maxLength={120}
                          aria-label="Rename dossier"
                          placeholder={t.title}
                        />
                        <span className="coach__switcher-item-actions coach__switcher-item-actions--rename">
                          <button
                            type="submit"
                            className="coach__switcher-item-act"
                            title="Save (Enter)"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="coach__switcher-item-act"
                            title="Cancel (Esc)"
                            onClick={cancelRename}
                          >
                            Cancel
                          </button>
                        </span>
                      </form>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="coach__switcher-item-main"
                          onClick={() => {
                            setOpen(false);
                            onSelect(t.id);
                          }}
                          disabled={renamingId !== null}
                        >
                          <span className="coach__switcher-item-title">{t.title}</span>
                          <span className="coach__switcher-item-meta">
                            {t.messageCount} msg · {formatDate(t.updatedAt)}
                          </span>
                        </button>
                        <span className="coach__switcher-item-actions">
                          <button
                            type="button"
                            className="coach__switcher-item-act"
                            title="Rename"
                            onClick={(e) => {
                              e.stopPropagation();
                              startRename(t);
                            }}
                          >
                            Rename
                          </button>
                          <button
                            type="button"
                            className="coach__switcher-item-act coach__switcher-item-act--danger"
                            title="Delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpen(false);
                              onDelete(t.id);
                            }}
                          >
                            Delete
                          </button>
                        </span>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
